import { ReactNode } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import styled from 'styled-components';

import { SelectField, TextAreaField, TextInputField } from '@electricjs/arc';

import { PriceField } from 'common/PriceField';
import { DateField } from 'common/DateField';

export enum AssetFieldTypes {
  Select = 'select',
  TextInput = 'text',
  TextArea = 'textarea',
  DatePicker = 'date',
  Price = 'price',
}

type AssetFormFieldProps = {
  fieldType?: AssetFieldTypes;
  options?: { value: string; label: string }[];
  name: string;
  isDisabled?: boolean;
  isLoading?: boolean;
  label: string;
  /** Rendered on the label row, right-aligned, without taking width from the field. */
  labelAddon?: ReactNode;
  placeholder?: string;
  isClearable?: boolean;
  overrides?: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onChange: (value: any) => void;
  };
};

// Positioned over the field's label row so the addon takes no width from the
// field itself (a sibling would squeeze the select and wrap its placeholder).
const LabelAddon = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
`;

const LabelAddonWrapper = styled.div`
  position: relative;
  flex-grow: 1;
  min-width: 0;
`;

const AssetFormFieldControl = ({
  fieldType,
  options = [],
  name,
  isDisabled,
  isLoading,
  label,
  placeholder,
  isClearable,
  overrides,
}: Omit<AssetFormFieldProps, 'labelAddon'>) => {
  const { control } = useFormContext();
  const value = useWatch({ control, name });

  switch (fieldType) {
    case AssetFieldTypes.Select:
      return (
        <SelectField
          id={`asset-select-${name}`}
          name={name}
          value={value}
          label={label}
          control={control}
          placeholder={placeholder}
          options={options}
          isLoading={isLoading}
          disabled={isLoading || isDisabled}
          isSearchable
          isClearable
        />
      );
    case AssetFieldTypes.DatePicker:
      return (
        <DateField
          id={`asset-date-picker-${name}`}
          name={name}
          control={control}
          label={label}
          placeholder={placeholder}
          isClearable={isClearable}
          value={value}
          disabled={isDisabled}
          minDate={null} // No min date
        />
      );
    case AssetFieldTypes.TextArea:
      return (
        <TextAreaField
          id={`asset-textarea-${name}`}
          rows={2}
          name={name}
          control={control}
          label={label}
          placeholder={placeholder}
          disabled={isDisabled}
        />
      );
    case AssetFieldTypes.Price:
      return (
        <PriceField
          id={`asset-input-${name}`}
          name={name}
          control={control}
          label={label}
          disabled={isDisabled}
          overrides={overrides}
        />
      );
    default:
      return (
        <TextInputField
          id={`asset-input-${name}`}
          name={name}
          control={control}
          label={label}
          placeholder={placeholder}
          disabled={isDisabled}
        />
      );
  }
};

export const AssetFormField = ({
  labelAddon,
  ...fieldProps
}: AssetFormFieldProps) =>
  labelAddon ? (
    <LabelAddonWrapper>
      <AssetFormFieldControl {...fieldProps} />
      <LabelAddon>{labelAddon}</LabelAddon>
    </LabelAddonWrapper>
  ) : (
    <AssetFormFieldControl {...fieldProps} />
  );

export default AssetFormField;
